import React, { useState, useRef } from 'react';
import { Upload, X, BookOpen, User, FileText, Type, Loader2, Clock } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function QuoteForm({ formData, onChange, calculatedDuration = 0 }) {
  const [dragActive, setDragActive] = useState(false);
  const [pdfDragActive, setPdfDragActive] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const fileInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handlePdfDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setPdfDragActive(true);
    } else if (e.type === 'dragleave') {
      setPdfDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handlePdfDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPdfDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await handlePdfFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        onChange({ coverUrl: e.target.result, coverFile: file });
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePdfFile = async (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Solo se permiten archivos PDF');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error('El archivo es demasiado grande (máx. 50MB)');
      return;
    }

    setIsAnalyzing(true);

    try {
      // Step 1: Upload file and get job_id
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const uploadRes = await axios.post(`${API}/analyze-pdf`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });

      const { job_id } = uploadRes.data;
      if (!job_id) throw new Error('No se recibió job_id del servidor');

      // Step 2: Poll for result
      let attempts = 0;
      const maxAttempts = 60; // 60 * 2s = 2 minutes max
      
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 2000));
        attempts++;
        
        try {
          const pollRes = await axios.get(`${API}/analyze-pdf/${job_id}`, { timeout: 10000 });
          
          if (pollRes.data.status === 'done') {
            const { word_count, page_count, content_pages, title, subtitle, author, copyright: copyrightInfo, isbn } = pollRes.data.result;
            const effectivePages = content_pages > 0 ? content_pages : page_count;
            
            const updates = { wordCount: word_count, pageCount: effectivePages };
            if (title) updates.bookTitle = subtitle ? `${title}: ${subtitle}` : title;
            if (author) updates.authorName = author;
            const copyrightParts = [copyrightInfo, isbn ? `ISBN: ${isbn}` : null].filter(Boolean);
            if (copyrightParts.length) updates.copyright = copyrightParts.join(' | ');
            
            onChange(updates);
            setAnalysisResult({
              filename: file.name,
              wordCount: word_count,
              pageCount: page_count,
              contentPages: content_pages,
              title, subtitle, author,
              copyright: copyrightInfo,
              isbn,
            });
            
            let desc = `${word_count.toLocaleString()} palabras, ${effectivePages} páginas`;
            if (title) desc += ` | ${title}`;
            if (author) desc += ` | ${author}`;
            
            if (page_count > 0 && word_count < page_count * 5) {
              toast.warning('PDF con pocas palabras', {
                description: `${page_count} páginas pero solo ${word_count} palabras. Si es escaneado, ingresa datos manualmente.`
              });
            } else {
              toast.success(`PDF analizado: ${file.name}`, { description: desc });
            }
            
            setIsAnalyzing(false);
            return;
          }
          // Still processing - continue polling
        } catch (pollErr) {
          // If polling endpoint returns error (400/404), the analysis failed
          if (pollErr.response?.status === 400) {
            throw new Error(pollErr.response?.data?.detail || 'Error en el análisis');
          }
          // Network errors during polling - keep trying
        }
      }
      
      // Timeout after all attempts
      throw new Error('El análisis tardó demasiado');
      
    } catch (error) {
      console.error('Error analyzing PDF:', error);
      toast.error(error.message || 'Error al analizar el PDF', {
        description: 'Puedes ingresar las palabras y páginas manualmente.'
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handlePdfInput = async (e) => {
    if (e.target.files && e.target.files[0]) {
      await handlePdfFile(e.target.files[0]);
    }
  };

  const removeCover = () => {
    onChange({ coverUrl: '', coverFile: null });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-5">
      {/* PDF Upload for auto-analysis */}
      <div className="space-y-2">
        <Label className="text-slate-300 flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#aa2aff]" />
          Subir Libro PDF (Análisis Automático)
        </Label>
        
        <div
          className={cn(
            "border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all",
            pdfDragActive 
              ? "border-[#aa2aff] bg-[#aa2aff]/10" 
              : "border-[#272b96] hover:border-[#aa2aff]/50 hover:bg-[#0f133a]/50",
            isAnalyzing && "pointer-events-none opacity-70"
          )}
          onDragEnter={handlePdfDrag}
          onDragLeave={handlePdfDrag}
          onDragOver={handlePdfDrag}
          onDrop={handlePdfDrop}
          onClick={() => !isAnalyzing && pdfInputRef.current?.click()}
          data-testid="pdf-dropzone"
        >
          {isAnalyzing ? (
            <div className="flex flex-col items-center justify-center gap-2 py-3">
              <Loader2 className="w-6 h-6 animate-spin text-[#aa2aff]" />
              <span className="text-sm text-slate-400">Analizando PDF...</span>
              <span className="text-xs text-slate-500">Esto puede tardar hasta 30 segundos para libros grandes</span>
            </div>
          ) : (
            <>
              <FileText className="w-6 h-6 mx-auto mb-1 text-slate-400" />
              <p className="text-sm text-slate-400">
                Arrastra tu libro PDF o <span className="text-[#aa2aff]">haz clic</span>
              </p>
              <p className="text-xs text-slate-500">Extrae palabras y páginas automáticamente</p>
            </>
          )}
          <input
            ref={pdfInputRef}
            type="file"
            accept=".pdf"
            onChange={handlePdfInput}
            className="hidden"
            data-testid="input-pdf-file"
          />
        </div>

        {analysisResult && (
          <div
            className="rounded-xl border border-[#aa2aff]/40 bg-[#0f133a]/60 p-4 space-y-2"
            data-testid="pdf-analysis-result"
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-[#aa2aff] uppercase tracking-wide">Resultado del análisis</p>
              <button
                onClick={() => setAnalysisResult(null)}
                className="text-slate-500 hover:text-white transition-colors"
                data-testid="btn-close-analysis"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
              <div className="col-span-2 flex justify-between gap-3">
                <span className="text-slate-400">Palabras</span>
                <span className="text-white font-bold" data-testid="analysis-word-count">{analysisResult.wordCount.toLocaleString()}</span>
              </div>
              <div className="col-span-2 flex justify-between gap-3">
                <span className="text-slate-400">Páginas</span>
                <span className="text-white font-bold" data-testid="analysis-page-count">{analysisResult.pageCount}{analysisResult.contentPages > 0 && analysisResult.contentPages !== analysisResult.pageCount ? ` (${analysisResult.contentPages} con contenido)` : ''}</span>
              </div>
              {analysisResult.title && (
                <div className="col-span-2 flex justify-between gap-3">
                  <span className="text-slate-400">Título</span>
                  <span className="text-white text-right" data-testid="analysis-title">{analysisResult.title}</span>
                </div>
              )}
              {analysisResult.subtitle && (
                <div className="col-span-2 flex justify-between gap-3">
                  <span className="text-slate-400">Subtítulo</span>
                  <span className="text-white text-right" data-testid="analysis-subtitle">{analysisResult.subtitle}</span>
                </div>
              )}
              {analysisResult.author && (
                <div className="col-span-2 flex justify-between gap-3">
                  <span className="text-slate-400">Autor</span>
                  <span className="text-white text-right" data-testid="analysis-author">{analysisResult.author}</span>
                </div>
              )}
              {analysisResult.isbn && (
                <div className="col-span-2 flex justify-between gap-3">
                  <span className="text-slate-400">ISBN</span>
                  <span className="text-white text-right" data-testid="analysis-isbn">{analysisResult.isbn}</span>
                </div>
              )}
              {analysisResult.copyright && (
                <div className="col-span-2 flex justify-between gap-3">
                  <span className="text-slate-400">Copyright</span>
                  <span className="text-slate-300 text-right text-xs" data-testid="analysis-copyright">{analysisResult.copyright}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Book Title */}
      <div className="space-y-2">
        <Label htmlFor="bookTitle" className="text-slate-300 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#aa2aff]" />
          Título del Libro
        </Label>
        <Input
          id="bookTitle"
          data-testid="input-book-title"
          placeholder="Ingresa el título del libro"
          value={formData.bookTitle}
          onChange={(e) => onChange({ bookTitle: e.target.value })}
          className="bg-[#080c2d]/50 border-[#272b96] text-white placeholder:text-slate-500 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff] h-11"
        />
      </div>

      {/* Author Name */}
      <div className="space-y-2">
        <Label htmlFor="authorName" className="text-slate-300 flex items-center gap-2">
          <User className="w-4 h-4 text-[#aa2aff]" />
          Nombre del Autor
        </Label>
        <Input
          id="authorName"
          data-testid="input-author-name"
          placeholder="Ingresa el nombre del autor"
          value={formData.authorName}
          onChange={(e) => onChange({ authorName: e.target.value })}
          className="bg-[#080c2d]/50 border-[#272b96] text-white placeholder:text-slate-500 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff] h-11"
        />
      </div>

      {/* Copyright */}
      <div className="space-y-2">
        <Label htmlFor="copyright" className="text-slate-300 flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#aa2aff]" />
          Copyright
        </Label>
        <Input
          id="copyright"
          data-testid="input-copyright"
          placeholder="Se extrae automáticamente del PDF"
          value={formData.copyright || ''}
          onChange={(e) => onChange({ copyright: e.target.value })}
          className="bg-[#080c2d]/50 border-[#272b96] text-white placeholder:text-slate-500 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff] h-11"
        />
      </div>

      {/* Word Count, Page Count & Duration in a grid */}
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="wordCount" className="text-slate-300 flex items-center gap-2">
            <Type className="w-4 h-4 text-[#aa2aff]" />
            Palabras
          </Label>
          <Input
            id="wordCount"
            type="number"
            data-testid="input-words"
            placeholder="Ej: 50000"
            value={formData.wordCount || ''}
            onChange={(e) => onChange({ wordCount: parseInt(e.target.value) || 0 })}
            className="bg-[#080c2d]/50 border-[#272b96] text-white placeholder:text-slate-500 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff] h-11"
            min="0"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pageCount" className="text-slate-300 flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#aa2aff]" />
            Páginas
          </Label>
          <Input
            id="pageCount"
            type="number"
            data-testid="input-pages"
            placeholder="Ej: 200"
            value={formData.pageCount || ''}
            onChange={(e) => onChange({ pageCount: parseInt(e.target.value) || 0 })}
            className="bg-[#080c2d]/50 border-[#272b96] text-white placeholder:text-slate-500 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff] h-11"
            min="0"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="manualDuration" className="text-slate-300 flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#aa2aff]" />
            Duración (hrs)
          </Label>
          <Input
            id="manualDuration"
            type="number"
            step="0.5"
            data-testid="input-manual-duration"
            placeholder={calculatedDuration > 0 ? calculatedDuration.toFixed(1) : "Ej: 5.5"}
            value={formData.manualDuration || ''}
            onChange={(e) => onChange({ manualDuration: parseFloat(e.target.value) || 0 })}
            className="bg-[#080c2d]/50 border-[#272b96] text-white placeholder:text-slate-500 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff] h-11"
            min="0"
          />
          {calculatedDuration > 0 && !formData.manualDuration && (
            <p className="text-xs text-slate-500">Auto: {calculatedDuration.toFixed(1)}h</p>
          )}
        </div>
      </div>

      {/* Cover Upload */}
      <div className="space-y-2">
        <Label className="text-slate-300 flex items-center gap-2">
          <Upload className="w-4 h-4 text-[#aa2aff]" />
          Portada del Libro (Opcional)
        </Label>
        
        {formData.coverUrl ? (
          <div className="relative inline-block">
            <img
              src={formData.coverUrl}
              alt="Book cover preview"
              className="w-24 h-36 object-cover rounded-lg shadow-lg border border-[#272b96]"
              data-testid="cover-preview"
            />
            <button
              onClick={removeCover}
              className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors"
              data-testid="btn-remove-cover"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div
            className={cn(
              "border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all",
              dragActive 
                ? "border-[#aa2aff] bg-[#aa2aff]/10" 
                : "border-[#272b96] hover:border-[#aa2aff]/50 hover:bg-[#0f133a]/50"
            )}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            data-testid="cover-dropzone"
          >
            <Upload className="w-6 h-6 mx-auto mb-1 text-slate-400" />
            <p className="text-sm text-slate-400">
              Arrastra una imagen o <span className="text-[#aa2aff]">haz clic</span>
            </p>
            <p className="text-xs text-slate-500">PNG, JPG hasta 5MB</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInput}
              className="hidden"
              data-testid="input-cover-file"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default QuoteForm;
